import { useEffect, useState } from 'react';
import type { ThemeName } from '@trajectory/ui';
import { useSettingsStore } from '@/stores/settingsStore';

/** Applies the user's theme preference to the document root as `data-theme`. */
export function useApplyTheme(): void {
  const themeMode = useSettingsStore((state) => state.themeMode);

  useEffect(() => {
    const root = document.documentElement;
    if (themeMode === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', themeMode);
    }
  }, [themeMode]);
}

function systemPrefersLight(): boolean {
  return window.matchMedia('(prefers-color-scheme: light)').matches;
}

/**
 * The theme actually in effect right now, resolving `'system'` against the OS
 * preference. Non-CSS consumers (uPlot axis colours, three.js materials) need
 * a concrete `'dark' | 'light'` value rather than the tri-state preference.
 */
export function useResolvedTheme(): ThemeName {
  const themeMode = useSettingsStore((state) => state.themeMode);
  const [systemIsLight, setSystemIsLight] = useState(systemPrefersLight);

  useEffect(() => {
    if (themeMode !== 'system') return undefined;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const listener = () => setSystemIsLight(mediaQuery.matches);
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, [themeMode]);

  if (themeMode === 'system') return systemIsLight ? 'light' : 'dark';
  return themeMode;
}
